import {useMemo} from 'react'
import {useCustomerId, useCustomerProductLists, useCustomerType} from '@salesforce/commerce-sdk-react'

export const WISH_LIST_TYPE = 'wish_list'
export const CUSTOM_FOLDER_TYPE = 'custom_1'

const onClient = typeof window !== 'undefined'

/**
 * Fetches all product lists for the authenticated customer and separates
 * "My Favourites" (type: wish_list) from custom folders (type: custom_1).
 *
 * Returns undefined when the customer is not authenticated (no customerId).
 */
export const useProductLists = ({enabled: enabledOption} = {}) => {
    const customerId = useCustomerId()
    const {customerType, isRegistered} = useCustomerType()

    const registeredGate =
        onClient &&
        Boolean(customerId) &&
        customerType === 'registered' &&
        isRegistered

    const queryEnabled = enabledOption === false ? false : registeredGate

    const {data, isLoading, error, refetch} = useCustomerProductLists(
        {parameters: {customerId: queryEnabled ? customerId : undefined}},
        {enabled: queryEnabled}
    )

    const allLists = queryEnabled ? data?.data ?? [] : []

    const wishList = useMemo(
        () => allLists.find((list) => list.type === WISH_LIST_TYPE) ?? null,
        [allLists]
    )

    const customFolders = useMemo(
        () => allLists
            .filter((list) => list.type === CUSTOM_FOLDER_TYPE)
            .sort((a, b) => new Date(b.creationDate) - new Date(a.creationDate)),
        [allLists]
    )

    /**
     * Total item count across ALL lists (My Favourites only — items in custom
     * folders also exist in My Favourites, so we avoid double-counting).
     */
    const totalItemCount = wishList?.customerProductListItems?.length ?? 0

    /**
     * Returns true if the customer already has a wish_list list created.
     * Used to guard against creating duplicates.
     */
    const hasWishList = wishList !== null

    return {
        allLists,
        wishList,
        customFolders,
        totalItemCount,
        hasWishList,
        isLoading: queryEnabled ? isLoading : false,
        error: queryEnabled ? error : null,
        refetch
    }
}
